/** Import/Export de Meus Pontos — CSV, GeoJSON e KML. */
import type { NautPoint, PointInput } from '@/api/endpoints/points';

export function download(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function pointsToGeoJSON(points: NautPoint[]): string {
  return JSON.stringify(
    {
      type: 'FeatureCollection',
      features: points.map((p) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
        properties: {
          name: p.name, type: p.type, description: p.description,
          visibility: p.visibility, icon: p.icon, color: p.color,
        },
      })),
    },
    null,
    2,
  );
}

function csvEscape(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function pointsToCSV(points: NautPoint[]): string {
  const head = ['name', 'type', 'description', 'latitude', 'longitude', 'visibility'];
  const rows = points.map((p) =>
    [p.name, p.type, p.description, p.latitude, p.longitude, p.visibility].map(csvEscape).join(','),
  );
  return '﻿' + [head.join(','), ...rows].join('\n');
}

const num = (v: unknown): number | null => {
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

function parseCSV(text: string): PointInput[] {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const sep = lines[0].includes(';') && !lines[0].includes(',') ? ';' : ',';
  const head = lines[0].split(sep).map((h) => h.trim().toLowerCase());
  const idx = (k: string) => head.findIndex((h) => h === k || h.startsWith(k));
  const iName = idx('name') >= 0 ? idx('name') : idx('nome');
  const iLat = idx('lat');
  const iLon = idx('lon') >= 0 ? idx('lon') : idx('lng');
  const iType = idx('type') >= 0 ? idx('type') : idx('tipo');
  const iDesc = idx('desc') >= 0 ? idx('desc') : idx('obs');
  const out: PointInput[] = [];
  for (let i = 1; i < lines.length; i++) {
    const c = lines[i].split(sep);
    const lat = num(c[iLat]); const lon = num(c[iLon]);
    if (lat == null || lon == null) continue;
    out.push({
      name: (c[iName] ?? `Ponto ${i}`).trim() || `Ponto ${i}`,
      latitude: lat, longitude: lon,
      type: iType >= 0 ? c[iType]?.trim() || null : null,
      description: iDesc >= 0 ? c[iDesc]?.trim() || null : null,
      source: 'import-csv',
    });
  }
  return out;
}

function parseGeoJSON(text: string): PointInput[] {
  const data = JSON.parse(text);
  const feats = data.type === 'FeatureCollection' ? data.features : [data];
  const out: PointInput[] = [];
  for (const f of feats ?? []) {
    const g = f.geometry;
    if (!g || g.type !== 'Point') continue;
    const [lon, lat] = g.coordinates;
    if (typeof lat !== 'number' || typeof lon !== 'number') continue;
    const pr = f.properties ?? {};
    out.push({
      name: pr.name || pr.nome || 'Ponto importado',
      latitude: lat, longitude: lon,
      type: pr.type || pr.tipo || null,
      description: pr.description || pr.obs || null,
      source: 'import-geojson',
    });
  }
  return out;
}

function parseKML(text: string): PointInput[] {
  const doc = new DOMParser().parseFromString(text, 'text/xml');
  const out: PointInput[] = [];
  for (const pm of Array.from(doc.getElementsByTagName('Placemark'))) {
    const coordEl = pm.getElementsByTagName('coordinates')[0];
    if (!coordEl) continue;
    const [lon, lat] = coordEl.textContent!.trim().split(',').map(Number);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const name = pm.getElementsByTagName('name')[0]?.textContent?.trim() || 'Ponto KML';
    const desc = pm.getElementsByTagName('description')[0]?.textContent?.trim() || null;
    out.push({ name, latitude: lat, longitude: lon, description: desc, source: 'import-kml' });
  }
  return out;
}

export function parsePointsFile(filename: string, text: string): PointInput[] {
  const ext = filename.toLowerCase().split('.').pop();
  if (ext === 'csv') return parseCSV(text);
  if (ext === 'kml') return parseKML(text);
  if (ext === 'geojson' || ext === 'json') return parseGeoJSON(text);
  // tenta detectar pelo conteúdo
  const t = text.trim();
  if (t.startsWith('{') || t.startsWith('[')) return parseGeoJSON(text);
  if (t.startsWith('<')) return parseKML(text);
  return parseCSV(text);
}
